import React, { useState, useEffect } from 'react';
import { X, Clock, Download, RotateCcw, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { fileService } from '../services/fileService';

function VersionHistoryModal({ isOpen, onClose, file }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (isOpen && file) {
      loadVersions();
    }
  }, [isOpen, file]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fileService.getFileVersions(file.id);
      setVersions(data);
    } catch (err) {
      setError('Failed to load versions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    try {
      setActionLoading(`download-${version.id}`);
      await fileService.downloadFileVersion(file.id, version.id, version.original_filename || file.original_filename);
      setSuccessMessage(`Downloaded version ${version.version_number}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to download version: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreVersion = async (version) => {
    if (!window.confirm(`Are you sure you want to restore to version ${version.version_number}? This will create a new version with this content.`)) {
      return;
    }

    try {
      setActionLoading(`restore-${version.id}`);
      await fileService.restoreFileVersion(file.id, version.id);
      setSuccessMessage(`Restored to version ${version.version_number}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadVersions(); // Reload to show new version
    } catch (err) {
      setError('Failed to restore version: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="bg-cyan-100 rounded-full p-2">
              <Clock className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Version History</h2>
              <p className="text-sm text-gray-500">{file?.original_filename}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="px-6 pt-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              Loading versions...
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No versions yet</h3>
              <p className="text-gray-500">
                File versions will appear here when you upload new versions
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className={`border rounded-lg p-4 ${
                    index === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-sm font-semibold ${
                          index === 0 ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          Version {version.version_number}
                        </span>
                        {index === 0 && (
                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Created:</span> {formatDate(version.created_at)}
                        </div>
                        <div>
                          <span className="font-medium">Size:</span> {formatFileSize(version.file_size)}
                        </div>
                        <div>
                          <span className="font-medium">Type:</span> {version.mime_type}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleDownloadVersion(version)}
                        disabled={actionLoading === `download-${version.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Download this version"
                      >
                        <Download className="w-4 h-4" />
                        {actionLoading === `download-${version.id}` ? 'Downloading...' : 'Download'}
                      </button>
                      
                      {index !== 0 && (
                        <button
                          onClick={() => handleRestoreVersion(version)}
                          disabled={actionLoading === `restore-${version.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                          title="Restore to this version"
                        >
                          <RotateCcw className="w-4 h-4" />
                          {actionLoading === `restore-${version.id}` ? 'Restoring...' : 'Restore'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {versions.length > 0 && `${versions.length} version${versions.length !== 1 ? 's' : ''} total`}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VersionHistoryModal;
